import { GridColDef } from "@mui/x-data-grid-premium";
import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import {
  CalculatedGeneratorStatusSource,
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import { prettyPrintMonthYear } from "util/dates";

import { CalculatedStatusCell } from "../CalculatedStatusCell";
import { CalculatedStatusBySource } from "../calculatedStatusSources";
import { ExpandableInfoContainerCombined } from "./ExpandableInfoSection";
import { GreySimpleGrid } from "./GreySimpleGrid";

/**
 * A month as the tile's comparison sections receive it. Both axes are optional
 * because each section queries only the one it compares on.
 */
export type StatusComparisonRow = {
  month: string;
  calculatedStatusBySource?: readonly CalculatedStatusBySource[] | null;
  reportedFederalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedFederalGeneratorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  federalSource?: CalculatedGeneratorStatusSource | null;
  reportedStateGeneratorStatus?: StateRegulatoryStatus | null;
  calculatedStateGeneratorStatus?: StateRegulatoryStatus | null;
  stateSource?: CalculatedGeneratorStatusSource | null;
};

/** Column weights for the narrow tile: month and EPA labels are short, so calculated columns get the room. */
const MONTH_FLEX = 0.8;
const EPA_FLEX = 0.7;
const CALCULATED_FLEX = 1.2;

/**
 * One month-by-month EPA-vs-calculated section of the generator status tile, shared by
 * all four sections. `extraColumns` are appended after the calculated column, for a
 * caller that has a further measurement to show beside it.
 */
export function StatusComparisonSection<T extends StatusComparisonRow>({
  label,
  infoTooltip,
  text,
  showWarningChip,
  disabled,
  axis,
  rows,
  extraColumns = [],
}: {
  label: string;
  infoTooltip: string;
  text: string;
  showWarningChip?: boolean;
  disabled?: boolean;
  axis: "federal" | "state";
  rows: readonly T[];
  extraColumns?: readonly GridColDef<T>[];
}) {
  const reportedColumn: GridColDef<T> =
    axis === "federal"
      ? {
          field: "reportedFederalGeneratorStatus",
          headerName: "EPA",
          description: "Reported to EPA",
          flex: EPA_FLEX,
          valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
        }
      : {
          field: "reportedStateGeneratorStatus",
          headerName: "EPA",
          description: "Reported to EPA",
          flex: EPA_FLEX,
          valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
        };

  const calculatedColumn: GridColDef<T> = {
    field:
      axis === "federal" ? "calculatedFederalGeneratorStatus" : "calculatedStateGeneratorStatus",
    headerName: "Calculated",
    description: "Calculated status from shipment data",
    flex: CALCULATED_FLEX,
    valueFormatter: (value) =>
      axis === "federal"
        ? federalWasteGeneratorStatusToLabel(value)
        : stateWasteGeneratorStatusToLabel(value),
    renderCell: ({ formattedValue, row }) => (
      <CalculatedStatusCell
        label={String(formattedValue)}
        sources={row.calculatedStatusBySource ?? []}
        axis={axis}
        effectiveSource={axis === "federal" ? row.federalSource : row.stateSource}
      />
    ),
  };

  return (
    <ExpandableInfoContainerCombined
      label={label}
      infoTooltip={infoTooltip}
      text={text}
      showWarningChip={showWarningChip}
      disabled={disabled}
    >
      <GreySimpleGrid
        getRowId={(row) => row.month}
        columns={[
          {
            field: "month",
            headerName: "Month",
            flex: MONTH_FLEX,
            valueFormatter: (value) => prettyPrintMonthYear(value),
          },
          reportedColumn,
          calculatedColumn,
          ...extraColumns,
        ]}
        rows={[...rows]}
      />
    </ExpandableInfoContainerCombined>
  );
}
